/**
 * F-0236 (AC-11): what the web does with the shell's own `connectivity` signal, apart from the
 * `screen`/`badge`/`theme` plumbing `ShellRuntime.tsx` already owns.
 *
 * Kept as a leaf, like `shellDetection.ts`: a plain DOM attribute read and a `CustomEvent`, so
 * `ApiService` and `GraphqlClient` can refuse a write without importing React or the shell bridge.
 */

/** Set on `<html>` while the app reports no network (`connectivity { online: false }`). */
export const SHELL_OFFLINE_ATTRIBUTE = 'data-shell-offline';

/** Whether the shell last told the web it has no network. `false` in a browser, always. */
export function isShellOffline(): boolean {
  if (typeof document === 'undefined') return false;
  return document.documentElement.hasAttribute(SHELL_OFFLINE_ATTRIBUTE);
}

/**
 * Dispatched whenever `ShellRuntime` applies a `connectivity` event, so a module that cannot poll
 * (`MaintenanceNotice`'s own `/maintenance` check is a request, and a request is exactly what must
 * not be sent while offline) can still show the offline state the instant the shell reports it,
 * rather than waiting for its next scheduled check to fail the same way.
 */
export const SHELL_CONNECTIVITY_EVENT = 'featit:shell-connectivity';

export function publishShellConnectivity(online: boolean): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent<boolean>(SHELL_CONNECTIVITY_EVENT, { detail: online }));
}

/**
 * F-0236 AC-11: "a write is refused, and not queued." Thrown rather than returned, so it reaches a
 * write's existing failure path — `describeActionFailure` reads it the same way it already reads
 * `ReaderFacingError` — instead of every call site growing its own offline branch.
 */
export class ShellOfflineWriteError extends Error {
  constructor() {
    super("You're offline.");
    this.name = 'ShellOfflineWriteError';
  }
}

/**
 * Call at the top of anything that sends a write. Throws before the request is built, so an
 * offline write is refused outright rather than attempted and left to fail on the network — the
 * concept's "never queued" is true because nothing is ever sent to queue.
 */
export function assertOnlineForWrite(): void {
  if (isShellOffline()) throw new ShellOfflineWriteError();
}
